import {
    Box,
    Button,
    Chip,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    Link,
    ListSubheader,
    MenuItem,
    OutlinedInput,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { DataGrid, gridClasses, GridColDef, GridColumnVisibilityModel, GridRenderCellParams } from '@mui/x-data-grid';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../store/store';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { RootState } from '../../reducers';
import {
    fetchClaimsData,
    fetchClaimsByClaimNumber,
    setClaimNumberSearchValue,
    setClaimManagementSelectedStatus,
    setDosStart,
    setDosEnd,
    setEncounterIdList,
    getStatuses,
    setShowClaimHistoryModal,
    statusValues,
    exportClaimsData,
    parseMolinaPdfToExcel,
} from '../../store/claimsManagement.slice';
import Loading from '../Shared/Loading';
import { parentUrl } from '../../utils/constants';
import MessageDisplay from '../Shared/MessageDisplay';
import ClaimEditResubmission from './ClaimEditResubmission';
import { fetchClaimResubmitEditData, setShowEditResubmitModal } from '../../store/claimResubmission.slice';
import ProcedureSignOffEncounter from '../Assessments/ProcedureSignOff/ProcedureSignOffEncounter';
import {
    fetchEncountersByDate,
    setDateOfService,
    setDraftEncounterList,
    setEncounterChange,
    setShowProcedureSignOffModal,
} from '../../store/procedureSignOffEncounter.slice';
import ContentPasteSearchIcon from '@mui/icons-material/ContentPasteSearch';
import ClaimHistoryModal from './ClaimHistoryModal';
import { getExportModels } from '../../utils/exportUtils';
import { setSelectedMember } from '../../store/memberDetail.slice';
import { fetchProvider } from '../../store/shared.slice';
import Authorization from '../../utils/Authorization';

const ClaimsManagement = () => {
    const { claimsData, claimNumberSearchValue, claimManagementSelectedStatus, dosStart, dosEnd } = useSelector((state: RootState) => state.claimsManagement);
    const { encounterChange } = useSelector((state: RootState) => state.procedureSignOffEncounter);
    const dispatch = useAppDispatch();
    const [loadingMessage, setLoadingMessage] = useState('Loading...');
    const [selectedClaim, setSelectedClaim] = useState<any>();
    const [isCreateClaim, setIsCreateClaim] = useState<boolean>(false);
    const [pageSize, setPageSize] = useState(15);
    const [page, setPage] = useState(0);
    const pdfUploadInputRef = useRef<HTMLInputElement | null>(null);
    const SUSAN_CORDTS_PROVIDER_ID = '57a4d2d157e972165c12a850';
    const authData = new Authorization();

    const diagnosisFormatHandler = (params: GridRenderCellParams<any>) => {
        return params.value ? (
            <Stack>
                <Typography>{params.value.Code} -</Typography>
                <Typography color={'#999'}>{params.value.Name}</Typography>
            </Stack>
        ) : (
            <Typography></Typography>
        );
    };

    const showEditResubmit = (claimNumber: string, memberId: string, dateOfService: string) => {
        setIsCreateClaim(false);
        dispatch(
            fetchClaimResubmitEditData(claimNumber, memberId, dateOfService, () => {
                dispatch(setShowEditResubmitModal(true));
            })
        );
    };

    const showProcedureSignOffEncounter = (placeOfService: number, status: string, memberId: string, providerId: string, dateOfService: string, engagementId?: string) => {
        setIsCreateClaim(false);
        dispatch(
            fetchEncountersByDate(placeOfService, status, memberId, providerId, dateOfService, () => {
                dispatch(setShowProcedureSignOffModal(true));
            }, engagementId)
        );
    };

    const columns: GridColDef[] = [
        {
            field: 'Edit',
            headerName: 'Edit',
            minWidth: 130,
            flex: 0.75,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Button
                        className="button-120"
                        variant="contained"
                        onClick={() => {
                            showProcedureSignOffEncounter(
                                params.row.PlaceOfService,
                                params.row.Status,
                                params.row.MemberId,
                                params.row.CreatedById,
                                params.row.DateOfService,
                                params.row.EngagementId
                            );
                        }}
                    >
                        Edit Encounter
                    </Button>
                );
            },
        },
        {
            field: 'EditClaim',
            headerName: 'Edit',
            minWidth: 130,
            flex: 0.75,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Button
                        className="button-120"
                        variant="contained"
                        disabled={(params.row.CHStatus !== 1 && params.row.CHStatus < 4) || params.row.CHStatus === 12}
                        onClick={() => {
                            showEditResubmit(params.row.TraceId, params.row.MemberId, params.row.DateOfService);
                        }}
                    >
                        Modify Claim
                    </Button>
                );
            },
        },
        {
            field: 'IsModified',
            headerName: 'Modified',
            minWidth: 80,
            flex: 0.75,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    params.row.IsModified && (
                        <ContentPasteSearchIcon
                            className="icon-claim-history"
                            onClick={() => {
                                setSelectedClaim(params.row);
                                console.log('Selected Claim:', params.row);
                                dispatch(setShowClaimHistoryModal(true));
                            }}
                        />
                    )
                );
            },
        },
        {
            field: 'DateProcessed',
            headerName: 'Date Processed',
            flex: 0.75,
        },
        {
            field: 'TraceId',
            headerName: 'Claim Number',
            flex: 1.35,
        },
        {
            field: 'EncounterStatusValue',
            headerName: 'Status',
            flex: 0.75,
        },
        {
            field: 'DateOfService',
            headerName: 'DOS',
            flex: 0.75,
        },
        {
            field: 'AHCCCSId',
            headerName: 'AHCCCS ID',
            flex: 0.75,
        },
        {
            field: 'MemberName',
            headerName: 'Member Name',
            minWidth: 200,
            flex: 1,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Link
                        target={'_blank'}
                        underline="hover"
                        href={`${parentUrl}provider/members-list/member-chart/${params.row.MemberId}/member-profile`}
                        color={'#337ab7'}
                    >
                        {params.value}
                    </Link>
                );
            },
        },
        {
            field: 'ContractingHealthPlan',
            headerName: 'Contracting Health Plan',
            flex: 1,
        },
        {
            field: 'CreatedBy',
            headerName: 'Creating Provider',
            flex: 1,
        },
        {
            field: 'CreatedDate',
            headerName: 'Created Date',
            flex: 1,
        },
        {
            field: 'ApprovedBy',
            headerName: 'Submitting Provider',
            flex: 1,
        },
        {
            field: 'ApprovedDate',
            headerName: 'Submitted Date',
            flex: 1,
        },
        {
            field: 'CPTCode',
            headerName: 'CPT Code',
            flex: 0.75,
        },
        {
            field: 'PlaceOfService',
            headerName: 'Place of Service',
            flex: 0.75,
        },
        {
            field: 'PrimaryDiagnosis',
            headerName: 'Primary DX',
            flex: 1,
            renderCell: diagnosisFormatHandler,
        },
        {
            field: 'SecondaryDiagnosis',
            headerName: 'Secondary DX',
            flex: 1,
            renderCell: diagnosisFormatHandler,
        },
        {
            field: 'TertiaryDiagnosis',
            headerName: 'Tertiary DX',
            flex: 1,
            renderCell: diagnosisFormatHandler,
        },
        {
            field: 'QuaternaryDiagnosis',
            headerName: 'Quaternary DX',
            flex: 0.75,
            renderCell: diagnosisFormatHandler,
        },
        {
            field: 'CHPErrorMessage',
            headerName: 'CHP Error Message',
            flex: 1,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <div style={{ maxWidth: '100%' }}>
                        <Typography sx={{ color: 'error.main' }}>{params.value}</Typography>
                    </div>
                );
            },
        },
        {
            field: 'CHPErrorMessageDate',
            headerName: 'CHP Error Date',
            flex: 1,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <div style={{ maxWidth: '100%' }}>
                        <Typography sx={{ color: 'error.main' }}>{params.value}</Typography>
                    </div>
                );
            },
        },
        {
            field: 'MessageWaystar',
            headerName: 'Last Message',
            flex: 1,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <div style={{ maxWidth: '100%' }}>
                        <Typography sx={{ color: 'error.main' }}>{params.row.Message}</Typography>
                    </div>
                );
            },
        },
    ];

    const baseColumns = {
        EncounterStatusValue: true,
        DateOfService: true,
        AHCCCSId: true,
        MemberName: true,
        ContractingHealthPlan: true,
        CreatedDate: true,
        CreatedBy: true,
        CPTCode: true,
        PlaceOfService: true,
    };

    const forReviewColumnModel = {
        ...baseColumns,
        Edit: true,
        PrimaryDiagnosis: true,
        SecondaryDiagnosis: true,
        TertiaryDiagnosis: true,
        QuaternaryDiagnosis: true,
        ApprovedBy: false,
        ApprovedDate: false,
        DateProcessed: false,
        TraceId: false,
        EncounterId: false,
        EditClaim: false,
        CHPErrorMessage: false,
        CHPErrorMessageDate: false,
        MessageWaystar: false,
        IsModified: false,
    };

    const toSubmitColumnModel = {
        ...forReviewColumnModel,
        EncounterId: true,
        TraceId: false,
        CreatedBy: false,
        CreatedDate: false,
        ApprovedBy: true,
        ApprovedDate: true,
        CHPErrorMessage: true,
        CHPErrorMessageDate: true,
        MessageWaystar: false,
        IsModified: false,
    };

    const acceptedClaimModel = {
        ...baseColumns,
        EncounterId: false,
        TraceId: true,
        PrimaryDiagnosis: false,
        SecondaryDiagnosis: false,
        TertiaryDiagnosis: false,
        QuaternaryDiagnosis: false,
        ApprovedBy: false,
        ApprovedDate: false,
        EditClaim: true,
        Edit: false,
        CHPErrorMessage: false,
        CHPErrorMessageDate: false,
        MessageWaystar: false,
        IsModified: true,
    };

    const rejectedColumnModel = {
        ...acceptedClaimModel,
        EncounterId: true,
        Edit: false,
        MessageWaystar: true,
    };

    const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(forReviewColumnModel);

    const changeHeaders = (paramStatus: string = null) => {
        if (!paramStatus) {
            paramStatus = claimManagementSelectedStatus;
        }

        const selectedManagementStatus = statusValues.find((x) => x.key === paramStatus);

        if (selectedManagementStatus.value === 1 && selectedManagementStatus.type === 'Encounter') {
            setColumnVisibilityModel(forReviewColumnModel);
        } else if (selectedManagementStatus.value === 2 || selectedManagementStatus.value === 0) {
            setColumnVisibilityModel(toSubmitColumnModel);
        } else if (
            (selectedManagementStatus.value === 1 && selectedManagementStatus.type === 'Claim') ||
            selectedManagementStatus.value === 4 ||
            selectedManagementStatus.value === 8 ||
            selectedManagementStatus.value === 9 ||
            selectedManagementStatus.value === 10 ||
            selectedManagementStatus.value === 13
        ) {
            setColumnVisibilityModel(acceptedClaimModel);
        } else if (
            selectedManagementStatus.value === 5 ||
            selectedManagementStatus.value === 6 ||
            selectedManagementStatus.value === 7 ||
            selectedManagementStatus.value === 11 ||
            selectedManagementStatus.value === 12
        ) {
            setColumnVisibilityModel(rejectedColumnModel);
        }
    };

    const searchByClaimNumber = (event: any) => {
        if (!event.key || event.key === 'Enter') {
            setLoadingMessage('Loading...');
            dispatch(fetchClaimsByClaimNumber(claimNumberSearchValue, changeHeaders));
        }
    };
    const searchClaims = () => {
        if (claimNumberSearchValue) {
            searchByClaimNumber({});
        } else {
            setLoadingMessage('Loading...');
            dispatch(fetchClaimsData(claimManagementSelectedStatus, dosStart, dosEnd));
            changeHeaders();
        }
    };

    const createClaim = () => {
        setIsCreateClaim(true);
        dispatch(setSelectedMember(null));
        dispatch(setDraftEncounterList({ PrimaryDiagnosis: undefined, Encounters: [] }));
        dispatch(setDateOfService(new Date()));
        dispatch(fetchProvider(SUSAN_CORDTS_PROVIDER_ID));
        dispatch(setShowProcedureSignOffModal(true));
        // fetchEncountersByDate(placeOfService, status, memberId, providerId, dateOfService, () => {
        //         dispatch(setShowProcedureSignOffModal(true));
        //     })
    };

    const exportClaims = () => {
        setLoadingMessage('Loading...');
        const exportModels = getExportModels(columns, columnVisibilityModel);
        dispatch(exportClaimsData(claimManagementSelectedStatus, dosStart, dosEnd, exportModels, claimNumberSearchValue));
    };

    const openParsePdfDialog = () => {
        pdfUploadInputRef.current?.click();
    };

    const onPdfSelected = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setLoadingMessage('Parsing PDF to Excel...');
            dispatch(parseMolinaPdfToExcel(file));
        }

        event.target.value = '';
    };

    useEffect(() => {
        if (encounterChange) {
            searchClaims();
            dispatch(setEncounterChange(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, encounterChange]);

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <MessageDisplay />
            <ClaimEditResubmission encounterStatus={statusValues.find((x) => x.key === claimManagementSelectedStatus).value} searchClaims={searchClaims} />
            <ProcedureSignOffEncounter
                isCreateClaim={isCreateClaim}
                encounterStatus={statusValues.find((x) => x.key === claimManagementSelectedStatus).value}
                searchClaims={searchClaims}
            />
            <ClaimHistoryModal claim={selectedClaim} />
            <Box>
                {<Loading message={loadingMessage} />}
                <Grid container justifyContent={'flex-end'} spacing={3} style={{ padding: '16px 16px 16px 16px' }}>
                    <Grid size={2} textAlign={'left'}>
                        <Typography variant="h5">
                            {statusValues.find((x) => x.key === claimManagementSelectedStatus).type === 'Claim'
                                ? 'Claims'
                                : 'Member Encounters'}
                        </Typography>
                        <Typography
                                variant="h6"
                                className={statusValues.find((x) => x.key === claimManagementSelectedStatus).value > 2 ? 'colorsuccess' : 'colorerror'}
                            >
                                {statusValues.find((x) => x.key === claimManagementSelectedStatus).value > 2 ? 'Exist in Waystar' : 'Not in Waystar'}
                        </Typography>
                        <br />
                        <Stack direction="column" alignItems={'left'} justifyContent={'flex-end'} spacing={3}>
                            {statusValues.find((x) => x.key === claimManagementSelectedStatus).type === 'Claim' ? (
                                <>
                                    <FormControl>
                                        <InputLabel htmlFor="outlined-adornment-password">Search By Claim Number</InputLabel>
                                        <OutlinedInput
                                            id="outlined-adornment-password"
                                            type="text"
                                            value={claimNumberSearchValue}
                                            onChange={(e) => {
                                                dispatch(setClaimNumberSearchValue(e.target.value as string));
                                            }}
                                            onKeyUp={(event: any) => {
                                                if (event.key === 'Enter') {
                                                    searchByClaimNumber(event);
                                                }
                                            }}
                                            label="Search By Claim Number"
                                        />
                                    </FormControl>
                                    <Typography>OR</Typography>
                                </>
                            ) : null}
                            <FormControl>
                                <InputLabel id="demo-simple-select-label">Status</InputLabel>
                                <Select
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    value={claimManagementSelectedStatus}
                                    label="Status"
                                    onChange={(e) => {
                                        dispatch(setClaimNumberSearchValue(''));
                                        dispatch(setClaimManagementSelectedStatus(e.target.value));
                                    }}
                                >
                                    {getStatuses().map((statusByType, index) => [
                                        <ListSubheader key={index}>{statusByType.Name}</ListSubheader>,
                                        statusByType.List.map((status) => {
                                            return (
                                                <MenuItem key={status.key} value={status.key} sx={{ paddingLeft: 4 }}>
                                                    {status.label}
                                                </MenuItem>
                                            );
                                        }),
                                    ])}
                                </Select>
                            </FormControl>
                            <FormControl>
                                <DesktopDatePicker
                                    label="DOS Start Date:"
                                    value={dosStart}
                                    minDate={new Date('2017-01-01')}
                                    onChange={(newValue) => {
                                        setClaimNumberSearchValue('');
                                        dispatch(setDosStart(newValue));
                                    }}
                                    slotProps={{ textField: {} }}
                                />
                            </FormControl>
                            <FormControl>
                                <DesktopDatePicker
                                    label="DOS End Date:"
                                    value={dosEnd}
                                    minDate={new Date('2017-01-01')}
                                    onChange={(newValue) => {
                                        setClaimNumberSearchValue('');
                                        dispatch(setDosEnd(newValue));
                                    }}
                                    slotProps={{ textField: {} }}
                                />
                            </FormControl>
                            <Button onClick={searchClaims} style={{ minWidth: '120px!important' }} variant="contained">
                                Search
                            </Button>
                                    <Button onClick={createClaim} style={{ minWidth: '120px!important' }} variant="contained">
                                        Create Claim
                                    </Button>
                                    <Button
                                        disabled={!claimsData || claimsData.length === 0}
                                        onClick={exportClaims}
                                        style={{ minWidth: '120px!important' }}
                                        variant="contained"
                                    >
                                        Export
                                    </Button>
                                    <input ref={pdfUploadInputRef} type="file" accept="application/pdf,.pdf" style={{ display: 'none' }} onChange={onPdfSelected} />
                                    <Button onClick={openParsePdfDialog} style={{ minWidth: '120px!important' }} variant="contained">
                                        Parse PDF to Excel
                                    </Button>
                        </Stack>
                    </Grid>
                    <Grid size={10}>
                        <div style={{ height: '88vh', width: '100%' }}>
                            <DataGrid
                                columnVisibilityModel={columnVisibilityModel}
                                onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
                                rows={claimsData}
                                columns={columns}
                                onRowSelectionModelChange={(ids) => {
                                    const idsArray = Array.isArray(ids) ? ids : ids && 'ids' in ids ? Array.from(ids.ids) : [];
                                    dispatch(setEncounterIdList(idsArray));
                                }}
                                paginationModel={{ pageSize: pageSize, page: page }}
                                onPaginationModelChange={(model) => {
                                    setPageSize(model.pageSize);
                                    setPage(model.page);
                                }}
                                pageSizeOptions={[5]}
                                getRowId={(row) => row.EncounterId}
                                getRowHeight={() => 'auto'}
                                sx={{
                                    [`& .${gridClasses.cell}`]: {
                                        py: 2,
                                    },
                                    '& .MuiDataGrid-columnHeaderTitleContainerContent .MuiCheckbox-root': {
                                        // display: 'none',
                                    },
                                }}
                            />
                        </div>
                    </Grid>
                </Grid>
            </Box>
        </LocalizationProvider>
    );
};

export default ClaimsManagement;
